/**
 * Base map style selection. Two choices:
 *  - "modern": Stadia Alidade Smooth (token in env) or OpenFreeMap Positron fallback.
 *  - "satellite": Esri World Imagery, free for all use with attribution. Provides
 *    high-resolution colour aerial coverage worldwide; for Flanders it surfaces
 *    Maxar imagery that's comparable to Google Hybrid without the licensing or
 *    API-key hassle.
 *
 * Stored in localStorage so the user's choice survives reloads.
 */

import type { StyleSpecification } from 'maplibre-gl';

export type BaseLayerId = 'modern' | 'satellite';

const STORAGE_KEY = 'maplens.baseLayer.v1';

export function loadBaseLayer(): BaseLayerId {
  if (typeof window === 'undefined') return 'modern';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'satellite' ? 'satellite' : 'modern';
}

export function saveBaseLayer(id: BaseLayerId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // QuotaExceeded — no-op.
  }
}

const ESRI_WORLD_IMAGERY =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const ESRI_ATTRIBUTION =
  '<a href="https://www.esri.com" target="_blank" rel="noopener noreferrer">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community';

/** Inline MapLibre style for the satellite base. Doesn't need an API key. */
export const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  sources: {
    'esri-world-imagery': {
      type: 'raster',
      tiles: [ESRI_WORLD_IMAGERY],
      tileSize: 256,
      maxzoom: 19,
      attribution: ESRI_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: 'esri-world-imagery-layer',
      type: 'raster',
      source: 'esri-world-imagery',
      minzoom: 0,
      maxzoom: 22,
    },
  ],
};

export function modernStyleUrl(): string {
  const stadiaKey = import.meta.env.VITE_STADIA_API_KEY;
  if (stadiaKey && stadiaKey.length > 0) {
    return `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${encodeURIComponent(stadiaKey)}`;
  }
  return 'https://tiles.openfreemap.org/styles/positron';
}

/** Resolve the active base style (URL string for hosted styles, inline object for satellite). */
export function styleForBase(id: BaseLayerId): string | StyleSpecification {
  return id === 'satellite' ? SATELLITE_STYLE : modernStyleUrl();
}
